import { useCallback } from 'react';
import { Implant, ImplantsByDelivery, SkipImplantCheckOutReason } from 'api/models/Implants';
import { Set, SetsByDelivery } from 'api/models/Sets';
import { DeliveryImplantInspection, DeliveryQuickCheckOutReason } from 'api/models/Deliveries';

const NON_SHIPPABLE_SET_QUICK_CHECKOUT_REASONS = [
  DeliveryQuickCheckOutReason.WithoutSaviCapture,
  DeliveryQuickCheckOutReason.SalesRep,
  DeliveryQuickCheckOutReason.NoReturnRequired,
];

const useGetShippablesOfType = (
  sets?: Set[],
  implants?: Implant[],
  deliveryImplantInspections?: Record<string, DeliveryImplantInspection>,
) => {
  // Generic function to transform a flat array of sets or implants into a grouped format
  const transformToGroupedByDelivery = useCallback(
    <T extends Set | Implant>(
      type_: 'sets' | 'implants',
      items?: T[],
    ): SetsByDelivery | ImplantsByDelivery | undefined => {
      if (!items) return undefined;

      return items.reduce((acc, item) => {
        const key = type_ == 'sets' ? (item as Set).kitInstanceId : (item as Implant).barcode;

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(item);
        return acc;
      }, {} as SetsByDelivery | ImplantsByDelivery);
    },
    [],
  );

  // Function to compute grouped shippables
  const getShippablesOfType = useCallback(
    <T extends SetsByDelivery | ImplantsByDelivery>(type_: 'sets' | 'implants', items?: T | Set[] | Implant[]): T => {
      const groupedItems =
        type_ == 'sets' && Array.isArray(items)
          ? (transformToGroupedByDelivery('sets', items as Set[]) as T)
          : type_ == 'implants' && Array.isArray(items)
          ? (transformToGroupedByDelivery('implants', items as Implant[]) as T)
          : (items as T);

      return Object.entries(groupedItems || {}).reduce<T>((acc, [deliveryId, items]) => {
        const deliveryImplantsCheckedOutReason =
          (type_ == 'implants' && deliveryImplantInspections?.[deliveryId]?.CHECK_OUT?.skippedReason) || undefined;

        const shippables = (items as (Set | Implant)[]).filter(s => {
          const isCheckedOut =
            type_ == 'sets'
              ? (s as Set).inspections?.CHECK_OUT?.status == 'CONFIRMED' || !!(s as Set).quickCheckedOut
              : !!(deliveryImplantsCheckedOutReason || (s as Implant).inspection?.CHECK_OUT);

          const setQuickCheckOutReason = (s as Set).quickCheckedOut?.reason;

          const shipThisItem =
            isCheckedOut &&
            !s.notificationsSent?.checkout &&
            !s.turnaroundOrigin &&
            (type_ == 'sets'
              ? !setQuickCheckOutReason || !NON_SHIPPABLE_SET_QUICK_CHECKOUT_REASONS.includes(setQuickCheckOutReason)
              : !s.quickCheckedOut &&
                (!deliveryImplantsCheckedOutReason ||
                  ![SkipImplantCheckOutReason.SalesRep, SkipImplantCheckOutReason.WithoutSaviCapture].includes(
                    deliveryImplantsCheckedOutReason,
                  )));

          return shipThisItem;
        });

        if (shippables.length) {
          acc[deliveryId] = shippables;
        }

        return acc;
      }, {} as T);
    },
    [deliveryImplantInspections, transformToGroupedByDelivery],
  );

  // Function to compute a flat list of shippable IDs
  const getShippableIds = useCallback(
    <T extends SetsByDelivery | ImplantsByDelivery>(
      type_: 'sets' | 'implants',
      items?: T | Set[] | Implant[],
    ): string[] => {
      const shippables = getShippablesOfType<T>(type_, items);

      return Object.values(shippables)
        .flat()
        .map(s => (type_ == 'sets' ? (s as Set).kitInstanceId : (s as Implant).barcode));
    },
    [getShippablesOfType],
  );

  // Function to check if there are any shippables across both sets and implants
  const haveShippables = useCallback(
    (type: 'sets' | 'implants' | 'both' = 'both', isSurgeryFlow = false): boolean => {
      // we have shippables if
      // - isSurgeryFlow, and
      // - type is implants or both, and
      // - we have any deliveryImplantInspections?.[deliveryId]?.CHECK_OUT?.skippedReason that is set but not in [SkipImplantCheckOutReason.SalesRep, SkipImplantCheckOutReason.WithoutSaviCapture]
      if (isSurgeryFlow && (type == 'implants' || type == 'both')) {
        const haveShippableImplantsWithSkippedCheckOutReason = Object.values(deliveryImplantInspections || {}).some(
          inspection =>
            inspection.CHECK_OUT?.skippedReason &&
            ![SkipImplantCheckOutReason.SalesRep, SkipImplantCheckOutReason.WithoutSaviCapture].includes(
              inspection.CHECK_OUT.skippedReason,
            ),
        );
        if (haveShippableImplantsWithSkippedCheckOutReason) {
          return true;
        }
      }

      const setsByDelivery = transformToGroupedByDelivery('sets', sets);
      const implantsByDelivery = transformToGroupedByDelivery('implants', implants);

      if (type == 'sets') {
        const shippableSets = getShippablesOfType('sets', setsByDelivery);
        return Object.keys(shippableSets).length > 0;
      }

      if (type == 'implants') {
        const shippableImplants = getShippablesOfType('implants', implantsByDelivery);
        return Object.keys(shippableImplants).length > 0;
      }

      // Check for both sets and implants
      const shippableSets = getShippablesOfType('sets', setsByDelivery);
      const shippableImplants = getShippablesOfType('implants', implantsByDelivery);
      return Object.keys(shippableSets).length > 0 || Object.keys(shippableImplants).length > 0;
    },
    [transformToGroupedByDelivery, sets, implants, getShippablesOfType, deliveryImplantInspections],
  );

  return { getShippablesOfType, getShippableIds, haveShippables };
};

export default useGetShippablesOfType;
